import {
  AdminDollarInput2,
  Button,
  CustomExpenseOccurrenceEntity,
  Disclaimer,
  Div,
  ErrorToast,
  lightTheme,
  ModalPortal,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';

/**
 * Edit one materialized month - PATCH /custom-expense-occurrences/:id.
 *
 * Only the amount is editable; name and color come from the definition. Works
 * for any month (past, current or future), which is the only way to correct a
 * month that has already been charged.
 */
export const EditOccurrenceModal = ({
  occurrence,
  isOpen,
  monthLabel,
  onClose,
  onSubmit,
}: {
  /** The month being edited; `null` keeps the modal closed. */
  occurrence: CustomExpenseOccurrenceEntity | null;
  /** Lets the parent hide the form without dropping `occurrence` (and with it
   *  the admin's typed input) while a save is in flight. */
  isOpen: boolean;
  /** Pre-formatted "Aug 2026" label for the month. */
  monthLabel: string;
  onClose: () => void;
  /** Resolves once persisted; rejects to keep the form open for a retry. */
  onSubmit: (id: number, amount: number) => Promise<void>;
}) => {
  const [amountCents, setAmountCents] = useState(0);

  // `isOpen` is deliberately not a dep: hiding the form during a save and
  // showing it again must not stomp on the admin's input.
  useEffect(() => {
    if (occurrence) setAmountCents(occurrence.amount);
  }, [occurrence?.id]);

  const submit = async () => {
    if (!occurrence) return;
    if (amountCents < 0) return ErrorToast('Amount cannot be negative.');
    if (amountCents === occurrence.amount)
      return ErrorToast('Nothing to update.');

    try {
      await onSubmit(occurrence.id, amountCents);
    } catch {
      // Parent surfaced the error toast; keep the form so the admin can retry.
    }
  };

  return (
    <ModalPortal
      isOpen={isOpen && !!occurrence}
      closeModal={onClose}
      padding="24px"
      logo={false}
      width="440px"
      modalOverflow="auto"
      header={`Edit ${monthLabel}`}
      // Above the manager modal (overlay 201 / content 202) so the occurrences
      // list stays mounted and expanded behind it.
      zIndex={203}
    >
      <Div $flex $column $gap="20px">
        <Div>
          <AdminDollarInput2
            label="Amount"
            value={amountCents}
            onValueChange={(val: number) => setAmountCents(val)}
            $textCenter
          />
          <Disclaimer $m="0" $mt="8" $center $color={lightTheme.lightGray}>
            Changes only {monthLabel}. Editing the expense amount later rewrites
            future months and overwrites this override.
          </Disclaimer>
        </Div>

        <Div $flex $gap="12px" $mt="8">
          <Button $selected $gray onClick={onClose} $w="100%">
            Cancel
          </Button>
          <Button $selected onClick={submit} $w="100%">
            Save
          </Button>
        </Div>
      </Div>
    </ModalPortal>
  );
};
